import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAccess } from '../lib/access';
import ViewsNav from '../components/ViewsNav';
import AccessInfo from '../components/AccessInfo';
import VendorCard from '../components/VendorCard';
import VendorModal from '../components/VendorModal';

// /admin — admins and owners only (design 2b). Every list and change is enforced by the database:
// admin_people() / RLS decide what each person sees and can change; the UI only mirrors that.
const TABS = ['People & access', 'Sign-in history', 'Access change log', 'Property settings', 'Vendors'];
const ROLE_LABEL = { admin: 'Admin', owner: 'Owner', manager: 'Manager', crew: 'Crew' };
const METHOD_LABEL = { google: 'Google', credential: 'Password', email_otp: 'Email code', password: 'Password', unknown: 'Unknown' };
const fmt = (ts) => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function Admin({ session, currentView, onNavigate }) {
  const [tab, setTab] = useState(TABS[0]);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    supabase.from('properties').select('*').order('name').then(({ data }) => setProperties(data || []));
  }, []);

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>
        <ViewsNav currentView={currentView} onNavigate={onNavigate} />
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>Sign Out</button>
        </div>
      </aside>

      <main className="main-content">
        <div className="hub-header"><h1 className="hub-title">Admin</h1></div>
        <div className="hub-tabs">
          {TABS.map(t => (
            <button key={t} className={`hub-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} data-tab={t}>{t}</button>
          ))}
        </div>
        <div className="content-area">
          {tab === 'People & access' && <People properties={properties} />}
          {tab === 'Sign-in history' && <SigninHistory />}
          {tab === 'Access change log' && <AccessLog properties={properties} />}
          {tab === 'Property settings' && <PropertySettings properties={properties} onSaved={p => setProperties(ps => ps.map(x => x.id === p.id ? p : x))} />}
          {tab === 'Vendors' && <VendorsAdmin />}
        </div>
      </main>
    </div>
  );
}

// ---- People & access ------------------------------------------------------------------------

function People({ properties }) {
  const access = useAccess();
  const [people, setPeople] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);       // add/edit form state
  const [added, setAdded] = useState(null);     // email just added -> show instructions

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_people');
    if (error) setError(error.message);
    setPeople(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Owners pick manager/crew on properties they own; admins pick anything.
  const roleChoices = access.isAdmin ? ['admin', 'owner', 'manager', 'crew'] : ['manager', 'crew'];
  const propertyChoices = access.isAdmin ? properties : properties.filter(p => access.roleFor(p.id) === 'owner');

  const openAdd = () => {
    setAdded(null);
    setForm({ mode: 'add', email: '', display_name: '', role: 'crew', property_id: propertyChoices[0]?.id || '', can_see_access_info: false });
  };
  const openEdit = (person, row) => {
    setAdded(null);
    setForm({ mode: 'edit', original: row, email: person.email, display_name: person.display_name || '', role: row.role,
              property_id: row.property_id || '', can_see_access_info: row.can_see_access_info });
  };

  const save = async () => {
    setError('');
    const email = form.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('Enter a valid email address.'); return; }
    const row = {
      email,
      display_name: form.display_name.trim() || null,
      role: form.role,
      property_id: form.property_id || null,
      can_see_access_info: form.role === 'crew' ? form.can_see_access_info : false,
    };
    let res;
    if (form.mode === 'add') {
      res = await supabase.from('user_access').insert(row).select();
    } else {
      let q = supabase.from('user_access').update(row).eq('email', email);
      q = form.original.property_id ? q.eq('property_id', form.original.property_id) : q.is('property_id', null);
      res = await q.select();
    }
    if (res.error) {
      setError(res.error.code === '23505' ? `${email} already has access there. Edit that row instead.` : res.error.message);
      return;
    }
    if (!res.data || res.data.length === 0) { setError("That change wasn't allowed."); return; }
    if (form.mode === 'add') setAdded(email);
    setForm(null);
    load();
  };

  const remove = async (person, row) => {
    if (!window.confirm(`Remove ${person.email}'s ${ROLE_LABEL[row.role]} access${row.property_name ? ` on ${row.property_name}` : ''}?`)) return;
    let q = supabase.from('user_access').delete().eq('email', person.email);
    q = row.property_id ? q.eq('property_id', row.property_id) : q.is('property_id', null);
    const { data, error } = await q.select();
    if (error) { setError(error.message); return; }
    if (!data || data.length === 0) { setError("That change wasn't allowed."); return; }
    load();
  };

  // No links that expire: just where to go and which email to use.
  const signInText = (email) =>
    `Go to ${window.location.origin}. If ${email} is a Google account, choose "Continue with Google". ` +
    `Otherwise choose "Email me a code", enter ${email}, and type in the code we email you.`;

  return (
    <div>
      <div className="content-header">
        <h2>People &amp; access</h2>
        <p>{people.length} people{!access.isAdmin && ' · you can change managers and crew on your properties'}</p>
      </div>
      {error && <div className="error-msg">{error}</div>}
      {propertyChoices.length > 0 && !form && (
        <button className="btn btn-primary" onClick={openAdd} data-action="add-person">+ Add person</button>
      )}

      {added && (
        <div className="success-msg" style={{ marginTop: 12 }} data-instructions>
          <strong>Added {added}.</strong> Send them this:
          <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>{signInText(added)}</pre>
          <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard?.writeText(signInText(added))}>Copy</button>
        </div>
      )}

      {form && (
        <div className="property-info-card" style={{ marginTop: 12, padding: 16 }} data-person-form>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input value={form.email} disabled={form.mode === 'edit'} onChange={e => setForm({ ...form, email: e.target.value })} name="email" />
            </div>
            <div className="form-group">
              <label>Name</label>
              <input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} name="display_name" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} name="role">
                {roleChoices.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Property</label>
              <select value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} name="property_id">
                {access.isAdmin && <option value="">All properties</option>}
                {propertyChoices.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {form.role === 'crew' && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <input type="checkbox" checked={form.can_see_access_info} name="can_see_access_info"
                onChange={e => setForm({ ...form, can_see_access_info: e.target.checked })} />
              Can see door code &amp; WiFi
            </label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={save} data-action="save-person">{form.mode === 'add' ? 'Add' : 'Save'}</button>
            <button className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="admin-table-wrap" style={{ marginTop: 16, overflowX: 'auto' }}>
        <table className="revenue-table" data-people>
          <thead>
            <tr><th>Person</th><th>Access</th><th>Status</th><th>Signs in with</th></tr>
          </thead>
          <tbody>
            {people.map(p => (
              <tr key={p.email} data-person={p.email}>
                <td><strong>{p.display_name || p.user_name || p.email}</strong><br /><span style={{ color: '#888', fontSize: 12 }}>{p.email}</span></td>
                <td>
                  {p.access.map(a => (
                    <div key={`${a.property_id}-${a.role}`} style={{ marginBottom: 4 }}>
                      {ROLE_LABEL[a.role]} · {a.property_name || 'All properties'}
                      {a.role === 'crew' && (a.can_see_access_info ? ' · codes on' : ' · codes off')}
                      {a.can_edit ? (
                        <>
                          {' '}<button className="icon-btn" onClick={() => openEdit(p, a)} data-action="edit">Edit</button>
                          <button className="icon-btn delete" onClick={() => remove(p, a)} data-action="remove">Remove</button>
                        </>
                      ) : <span style={{ color: '#aaa', fontSize: 12 }}> (view only)</span>}
                    </div>
                  ))}
                </td>
                <td>{p.has_signed_in ? `Last active ${fmt(p.last_active)}` : "Invited — hasn't signed in yet"}</td>
                <td>{[...(p.sign_in_methods || []).map(m => METHOD_LABEL[m] || m), 'Email code'].filter((v, i, a) => a.indexOf(v) === i).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Sign-in history --------------------------------------------------------------------------

function SigninHistory() {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    supabase.from('signin_events').select('*').order('signed_in_at', { ascending: false }).limit(200)
      .then(({ data }) => setEvents(data || []));
  }, []);
  if (!events) return <p>Loading...</p>;
  return (
    <div>
      <div className="content-header"><h2>Sign-in history</h2><p>{events.length} sign-ins, newest first</p></div>
      {events.length === 0 ? <div className="empty-state"><h3>No sign-ins recorded yet</h3></div> : (
        <table className="revenue-table" data-signins>
          <thead><tr><th>When</th><th>Who</th><th>How</th><th>Device</th></tr></thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id}>
                <td>{fmt(e.signed_in_at)}</td>
                <td>{e.email}</td>
                <td>{METHOD_LABEL[e.method] || 'Unknown'}</td>
                <td style={{ color: '#888', fontSize: 12 }}>{(e.user_agent || '').slice(0, 60)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- Access change log ------------------------------------------------------------------------

function describeChange(e) {
  const o = e.old_row || {}, n = e.new_row || {};
  if (e.action === 'grant') return `${ROLE_LABEL[n.role] || n.role}${n.role === 'crew' ? (n.can_see_access_info ? ', codes on' : ', codes off') : ''}`;
  if (e.action === 'revoke') return `removed ${ROLE_LABEL[o.role] || o.role}`;
  const parts = [];
  if (o.role !== n.role) parts.push(`${ROLE_LABEL[o.role]} → ${ROLE_LABEL[n.role]}`);
  if (o.can_see_access_info !== n.can_see_access_info) parts.push(n.can_see_access_info ? 'codes on' : 'codes off');
  if (o.display_name !== n.display_name) parts.push('name');
  if (o.property_id !== n.property_id) parts.push('property');
  return parts.join(', ') || 'no visible change';
}

function AccessLog({ properties }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    supabase.from('access_log').select('*').order('at', { ascending: false }).limit(200)
      .then(({ data }) => setRows(data || []));
  }, []);
  const propName = (id) => id ? (properties.find(p => p.id === id)?.name || 'a property') : 'All properties';
  if (!rows) return <p>Loading...</p>;
  return (
    <div>
      <div className="content-header"><h2>Access change log</h2><p>{rows.length} changes, newest first</p></div>
      <table className="revenue-table" data-access-log>
        <thead><tr><th>When</th><th>Changed by</th><th>What</th><th>Whose access</th><th>Property</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>{fmt(r.at)}</td>
              <td>{r.actor_email}</td>
              <td>{r.action}: {describeChange(r)}</td>
              <td>{r.target_email}</td>
              <td>{propName(r.property_id)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Property settings ------------------------------------------------------------------------

const PROPERTY_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'address', label: 'Address' },
  { key: 'platform', label: 'Platform' },
  { key: 'listing_url', label: 'Listing URL' },
  { key: 'checkin_time', label: 'Check-in time' },
  { key: 'checkout_time', label: 'Check-out time' },
  { key: 'bedrooms', label: 'Bedrooms', number: true },
  { key: 'bathrooms', label: 'Bathrooms', number: true },
  { key: 'max_guests', label: 'Max guests', number: true },
  { key: 'sqft', label: 'Sq ft', number: true },
];

function PropertySettings({ properties, onSaved }) {
  const access = useAccess();
  const mine = properties.filter(p => access.can(p.id, 'admin'));
  return (
    <div>
      <div className="content-header"><h2>Property settings</h2></div>
      {mine.map(p => <PropertySettingsCard key={p.id} property={p} onSaved={onSaved} />)}
    </div>
  );
}

function PropertySettingsCard({ property, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(PROPERTY_FIELDS.map(f => [f.key, property[f.key] ?? ''])));
  const [msg, setMsg] = useState('');
  const save = async () => {
    const payload = {};
    PROPERTY_FIELDS.forEach(f => {
      const v = form[f.key];
      payload[f.key] = f.number ? (v === '' ? null : parseFloat(v)) : (String(v).trim() || null);
    });
    if (!payload.name) { setMsg('Name is required.'); return; }
    const { data, error } = await supabase.from('properties').update(payload).eq('id', property.id).select('*').single();
    if (error) { setMsg(error.message); return; }
    setMsg('Saved.');
    onSaved(data);
  };
  return (
    <div className="property-info-card" style={{ marginBottom: 16 }} data-property-settings={property.id}>
      <div className="property-info-body">
        <h3 style={{ marginTop: 0 }}>{property.name}</h3>
        <div className="property-info-grid">
          {PROPERTY_FIELDS.map(f => (
            <div key={f.key} className="property-info-row">
              <label className="property-info-label">{f.label}</label>
              <input className="property-info-input" type={f.number ? 'number' : 'text'} value={form[f.key]}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })} name={f.key} />
            </div>
          ))}
        </div>
        <button className="btn btn-primary" onClick={save} style={{ marginTop: 12 }}>Save details</button>
        {msg && <span style={{ marginLeft: 12 }}>{msg}</span>}
        <h4>Door code &amp; WiFi</h4>
        <AccessInfo propertyId={property.id} editable />
      </div>
    </div>
  );
}

// ---- Vendors ------------------------------------------------------------------------------------

function VendorsAdmin() {
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(undefined);   // undefined = closed, null = new, object = edit
  const load = useCallback(async () => {
    const { data } = await supabase.from('vendors').select('*').order('name');
    setVendors(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await supabase.from('vendors').delete().eq('id', id);
    load();
  };
  return (
    <div>
      <div className="content-header"><h2>Vendors</h2><p>{vendors.length} vendors</p></div>
      <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add vendor</button>
      <div className="vendor-list" style={{ marginTop: 12 }}>
        {vendors.map(v => <VendorCard key={v.id} vendor={v} onEdit={setEditing} onDelete={remove} />)}
      </div>
      {editing !== undefined && (
        <VendorModal vendor={editing} onSaved={() => { setEditing(undefined); load(); }} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}
